import { cloneElement, FC, isValidElement, MouseEvent, ReactElement, ReactNode } from 'react';

import { SmallTooltip } from '@/shared/components/tooltips';

type AccessState = {
    allowed?: boolean;
    disabled?: boolean;
    reason?: string | null;
};

interface PermissionGuardProps {
    access?: AccessState;
    children: ReactElement;
    disabled?: boolean;
    disabledReason?: ReactNode;
    tooltipPlacement?: 'top' | 'topLeft' | 'topRight' | 'bottom' | 'bottomLeft' | 'bottomRight' | 'left' | 'right';
}

const reasonMap: Record<string, ReactNode> = {
    INSUFFICIENT_PERMISSIONS: 'You do not have permission to perform this action.',
    RESOURCE_NOT_LOADED: 'This resource is not available yet.',
    NESTED_RESOURCE_NOT_LOADED: 'This resource is not available yet.',
    RESOURCE_LOCKED: 'Unlock this resource to make changes.',
    PARENT_RESOURCE_NOT_LOADED: 'The parent resource is not available yet.',
};

const getReason = (access?: AccessState, disabledReason?: ReactNode) => {
    if (disabledReason) {
        return disabledReason;
    }
    if (!access?.reason) {
        return reasonMap.INSUFFICIENT_PERMISSIONS;
    }

    return reasonMap[access.reason] ?? reasonMap.INSUFFICIENT_PERMISSIONS;
};

const PermissionGuard: FC<PermissionGuardProps> = ({
    access,
    children,
    disabled,
    disabledReason,
    tooltipPlacement = 'top',
}) => {
    const isDisabled = Boolean(disabled || access?.disabled || access?.allowed === false);

    if (!isValidElement(children)) {
        return children;
    }

    const child = children as ReactElement<{
        disabled?: boolean;
        onClick?: (event: MouseEvent<HTMLElement>) => void;
    }>;

    const guardedChild = cloneElement(child, {
        disabled: isDisabled || child.props?.disabled,
        onClick: isDisabled ? undefined : child.props?.onClick,
    });

    if (!isDisabled) {
        return guardedChild;
    }

    return (
        <SmallTooltip title={getReason(access, disabledReason)} placement={tooltipPlacement} padding='0.25rem 0'>
            <span
                style={{
                    cursor: 'not-allowed',
                    display: 'inline-flex',
                    lineHeight: 0,
                }}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
            >
                {guardedChild}
            </span>
        </SmallTooltip>
    );
};

export default PermissionGuard;
